<template>
  <b-container fluid class="document-view">
    <div class="mt-3">
      <b-btn-close @click="close()"></b-btn-close>
      <div class="clearfix"></div>
    </div>

    <b-row>
      <b-col v-if="loading" cols="8" class="text-center">
        <b-spinner variant="secondary-orange" class="mt-5"></b-spinner>
      </b-col>

      <b-col v-if="document" cols="8">
        <div class="d-flex align-items-center justify-content-between mb-3 mt-4">
          <h1 class="m-0">
            Document details
          </h1>
          <b-btn-group>
            <b-btn
              v-if="url"
              variant="outline-secondary"
              size="sm"
              :href="url"
              target="_blank"
            >
              <i class="fa fa-external-link-alt" /> Source
            </b-btn>
            <b-btn
              v-if="isFavorite"
              variant="outline-secondary"
              size="sm"
              @click="unfavoriteDocument"
            >
              <i class="fa fa-star" /> Remove from bookmarks
            </b-btn>
            <b-btn
              v-else
              variant="outline-secondary"
              size="sm"
              @click="favoriteDocument"
            >
              <i class="far fa-star" /> Add to bookmarks
            </b-btn>
            <b-dropdown
              text="Add to collection"
              variant="outline-secondary"
              size="sm"
            >
              <b-dropdown-item
                v-for="c in collections"
                :key="c._id"
                v-b-popover.hover.right="{
                  title: c.description,
                  delay: { show: 500, hide: 50 }
                }"
                :class="{
                  'dropdown-icon': documentCollectionIds.includes(c._id)
                }"
                @click="addToOrDeleteFromCollection(c)"
              >
                <i
                  v-if="documentCollectionIds.includes(c._id)"
                  class="fa fa-check"
                />
                <span
                  :class="{
                    'font-weight-bold': documentCollectionIds.includes(c._id)
                  }"
                  >{{ c.name }}</span
                >
              </b-dropdown-item>
              <b-dropdown-item
                class="dropdown-icon"
                @click="openCollectionNameModal"
              >
                <i class="fa fa-plus" />
                New collection…
              </b-dropdown-item>
            </b-dropdown>
          </b-btn-group>
        </div>
        <hr>
        <DocumentDetailTextNode
          v-for="(node, i) in document.text"
          v-show="!node.hide"
          :key="i"
          class="node"
          :class="node.class"
          :node="node"
          :words="wordsToHiLite"
        ></DocumentDetailTextNode>
        
      </b-col>
      <b-col cols="4" class="mt-4">
        <div class="whitecard">
          <div class="mb-4">
            <b-btn
              :variant="showSimilarDocuments ? 'primary' : 'outline-secondary'"
              size="sm"
              @click="toggleSimilarDocuments"
            >
              Similar documents
            </b-btn>
          </div>
          <div v-if="showSimilarDocuments">
            <b-table-simple class="small">
              <p v-if="!similarDocuments" class="text-center">Loading…</p>
              <tr v-for="doc in similarDocuments" :key="doc.webkey">
                <td
                  class="p-1"
                  :class="{ 'font-weight-bold': doc.webkey === localWebkey }"
                  @click="changeDocument(doc.webkey)"
                >
                  <b-link class="text-dark">
                    {{ doc.title }}
                  </b-link>
                </td>
                <td class="p-1 text-right">
                  <b-btn
                    variant="link-dark"
                    size="sm"
                    class="py-0 px-1"
                    @click="changeDocument(doc.webkey)"
                  >
                    <i class="fa fa-eye" />
                  </b-btn>
                </td>
              </tr>
            </b-table-simple>
          </div>
          <div v-if="!showSimilarDocuments && document">
            <p
              v-for="(value, key) in document.meta"
              v-show="!value.hide"
              :key="key"
            >
              <span class="small">{{ getMetaName(value, key) }}</span
              ><br />
              <draggable
                :list="getMetaValues(value, key)"
                :group="{ name: 'annotations', pull: 'clone', put: false }"
              >
                <b-badge
                  v-for="(metaValues, i) in getMetaValues(value, key)"
                  :key="i"
                  class="mr-1 cursor-grab"
                  variant="light"
                  @click="showOptionsPanel(metaValues)"
                >
                  {{ metaValues.name }}
                </b-badge>
              </draggable>
            </p>
          </div>
        </div>
      </b-col>
    
    </b-row>
    
    <InputMultipleModal
      :header="'Create collection'"
      :fields="collectionModalFields"
      :size="'sm'"
      @submit="submitCollectionNameModal"
    ></InputMultipleModal>
  </b-container>
</template>

<script>
import _ from "lodash";
import draggable from "vuedraggable";
import { mapActions, mapState } from "vuex";
import { EventBus } from "@/eventBus";
import DocumentDetailTextNode from "./DocumentDetailTextNode";
import InputMultipleModal from "./InputMultipleModal";

const UNKNOWN_INDICATOR = "<UNK>";

export default {
  name: "DocumentDetail",
  components: {
    draggable,
    DocumentDetailTextNode,
    InputMultipleModal
  },
  props: {
    webkey: { type: String, required: true },
    selection: { type: Object, required: true }
  },
  data() {
    return {
      localWebkey: null,
      loading: true,
      showSimilarDocuments: null,
      document: null,
      similarDocuments: [],
      collectionModalFields: [
        {
          name: "name",
          label: "Name",
          value: ""
        },
        {
          name: "description",
          label: "Description",
          value: ""
        }
      ]
    };
  },
  computed: {
    ...mapState({
      apiId: state => state.api.apiId,
      collections: state => state.collection.collections,
      documents: state => state.document.documents,
      favorites: state => state.favorite.favorites,
      filterFocused: state => state.filter.filterFocused
    }),
    isFavorite() {
      if (this.document && this.favorites && this.favorites.length) {
        return this.favorites.find(f => f === this.localWebkey);
      }

      return false;
    },
    documentCollectionIds() {
      if (this.document && this.collections && this.collections.length) {
        return this.collections
          .filter(c => c.documents.includes(this.localWebkey))
          .map(c => c._id);
      }

      return [];
    },
    url() {
      if (!this.document) {
        return null;
      }

      const urloc = this.document.text.find(t => t.name.toLowerCase() === "url");
      if(urloc){
        return urloc.text
      }
      else{
        return null
      }
    },
    wordsToHiLite(){
      let words = [];
      if(this.filterFocused){
        words = this.filterFocused.rawquery.map(item => {
          if(typeof item === 'object' && item !== null){
            return item.name;
          }
        })
      }
      if(this.selection){
        words.push(this.selection.name);
      }
      return words;
    }
  },
  watch: {
    webkey(newVal) {
      this.changeDocument(newVal);
    }
  },
  mounted() {
    this.localWebkey = this.webkey;
    this.load();

    this.$root.$on("bv::modal::hide", () => {
      this.showSimilarDocuments = false;
    });
  },
  methods: {
    ...mapActions([
      "loadCollections",
      "loadDocument",
      "loadSimilarDocuments",
      "saveFavorite",
      "deleteFavorite",
      "saveCollection",
      "addDocumentToCollection",
      "removeDocumentFromCollection"
    ]),
    load() {
      this.loading = true;
      this.document = null;

      this.loadDocument({ doc: this.localWebkey }).then(document => {
        this.parseDocument(document);
      });
    },
    parseDocument(document) {
      _.each(document.text, node => {
        if (node.text === "" || node.text === UNKNOWN_INDICATOR) {
          node.hide = true;
          return;
        }
        switch (node.name.toLowerCase()) {
          case "authors":
            if (node.text.length > 100) {
              node.collapsible = true;
              node.collapsed = true;
            }
            break;
          case "title":
            node.title = true;
            break;
          case "url":
            node.url = true;
            break;
        }

        node.class = "size-" + node.layout.replace("<", "").replace(">", "");
      });

      _.each(document.meta, (node, key) => {
        if (
          key === "TTC_Concept" ||
          key === "TTC_Theme" ||
          key === "TTC_Topic"
        ) {
          node.hide = true;
        }

        if (
          node.length === 1 &&
          (node[0][0].name === "" || node[0][0].name === UNKNOWN_INDICATOR)
        ) {
          node.hide = true;
        }
      });

      this.document = document;

      this.loading = false;
    },
    getMetaName(value, key) {
      return key.split("_")[1];
    },
    getMetaValues(values) {
      const v = values.map(meta => meta[0]);
      return v;
    },
    favoriteDocument() {
      this.saveFavorite({ webkey: this.localWebkey });
    },
    unfavoriteDocument() {
      this.deleteFavorite({ webkey: this.localWebkey });
    },
    addToOrDeleteFromCollection(collection) {
      if (this.documentCollectionIds.includes(collection._id)) {
        this.removeDocumentFromCollection({
          collectionId: collection._id,
          documentId: this.localWebkey
        });
      } else {
        this.addDocumentToCollection({
          collectionId: collection._id,
          documentId: this.localWebkey
        });
      }
    },
    toggleSimilarDocuments() {
      if (this.showSimilarDocuments) {
        this.showSimilarDocuments = false;
      } else {
        this.loadSimilarDocuments({ docs: [this.localWebkey] }).then(
          similarDocuments => {
            let mappedDocuments = [];
            let count = 0;
            _.each(similarDocuments.author, () => {
              mappedDocuments.push({
                author: similarDocuments.author[count],
                citations: similarDocuments.citations[count],
                coor: similarDocuments.coor[count],
                familycitations: similarDocuments.familycitations[count],
                os: similarDocuments.os[count],
                pubdate: similarDocuments.pubdate[count],
                title: similarDocuments.title[count],
                type: similarDocuments.type[count],
                url: similarDocuments.url[count],
                webkey: similarDocuments.webkey[count]
              });
              count++;
            });
            this.similarDocuments = mappedDocuments;
            this.showSimilarDocuments = true;
          }
        );
      }
    },
    changeDocument(webkey) {
      if (this.localWebkey === webkey) {
        return;
      }

      this.localWebkey = webkey;
      this.load();
    },
    openCollectionNameModal() {
      this.$bvModal.show("modal-input");
    },
    submitCollectionNameModal() {
      this.saveCollection({
        collection: {
          name: this.collectionModalFields.find(f => f.name === "name").value,
          description: this.collectionModalFields.find(
            f => f.name === "description"
          ).value,
          documents: [this.localWebkey]
        }
      }).then(() => {
        this.$toasted.show("Collection created");

        this.collectionModalFields.forEach(f => {
          f.value = "";
        });
      });
    },
    close() {
      this.showSimilarDocuments = false;
      this.$emit("close");
    },
    showOptionsPanel(meta) {
      EventBus.$emit("showOptions", meta);
    }
  }
};
</script>
